import PropTypes from 'prop-types';
import { useEffect } from 'react';
import MojitoCore from 'mojito/core';
import { useDispatch } from 'react-redux';
import MojitoPresentation from 'mojito/presentation';

const intentActions = MojitoCore.Intents.actions;
const { IntentActionTrigger, Types: IntentTypes } = MojitoPresentation.Base.Intent;

/**
 * This component is used to preserve search parameters while navigating between pages.
 *
 * @example <caption>Render a Custom Redirect component.</caption>
 *
 * const path = '/sports/football';
 * render() {
 *   return <CustomRedirect to={path} />
 * }
 * // Redirects to /sports/football while keeping the URL query/search parameters.
 *
 *
 * @example <caption>Another example</caption>
 *
 * <CustomRedirect
 *  to={"/home"}
 * />
 *
 * // If the path matches exactly "/index.html", the page will be redirected to /home while keeping URL query/search parameters.
 *
 *
 * @class CustomRedirect
 * @memberof Mojito.Presentation.Components
 */

export default function CustomRedirect({ toNav, isReplace = false }) {
    const dispatch = useDispatch();
    useEffect(() => {
        const data = IntentActionTrigger.buildDataPayload(toNav, isReplace);
        dispatch(intentActions.publishIntent(IntentTypes.VIEW, data));
    }, [toNav, isReplace, dispatch]);
    return null;
}

/**
 * CustomRedirect prop types.
 *
 * @property {Mojito.Presentation.Base.Navigation.types.NavigationPayload} toNav - Navigation payload.
 * @property {boolean} [isReplace = false] - Replaces the url in history instead of adding a new one.
 * @memberof Mojito.Presentation.Components.CustomRedirect
 */
CustomRedirect.propTypes = {
    toNav: PropTypes.object.isRequired,
    isReplace: PropTypes.bool,
};
